import { useContext, useEffect, useMemo, useRef, useState } from "react";
import Router, { useRouter } from "next/router";
import { ApolloError } from "@apollo/client";
import { AuthContext, AuthStatus, SessionInfo } from "@/contexts/AuthContext";
import {
  useSignInWithBizsolMutation,
  useConnectToBizsolMutation,
  useSwitchBizsolIdMutation,
  RegistrationTokenType,
} from "@/generated/graphql";
import Rollbar from "rollbar";
import { useSnackBar } from "@moneyforward/mfx_biz_react_components";
import {
  ErrorCode,
  errorCodeIsIncluded,
  extractErrExtraInfo,
  extractBizsolRqeustErrorMessage,
  BackendErrorExtraInfoKeys,
  moveToBizsolRequestErrorPage,
  moveToErrorPage,
} from "@/libs/errors";
import { getUrlWithQuery } from "@/libs/url";
import useFixedAuth from "@/libs/useFixedAuth";
import { setLoginSource, LoginSource } from "@/libs/loginSource";
import config from "@/libs/config";
import { useAmIContractAdminNoCallback } from "@/components/hooks/useAmIContractAdmin";
import { useUpdateContractAdminStatus } from "@/components/hooks/useUpdateContractAdminStatus";
import { BizsolSwitchIDModal } from "@/components/organisms/BizsolSwitchIDModal";
import useRollbarUtil from "../hooks/useRollbarUtil";

type BizsolCallBackPageQueryParams = {
  code: string | string[] | null;
  state: string | string[] | null;
  iss: string | string[] | null;
};

const getCodeStrFromQueryParamCode = (code: string | string[]) => {
  return Array.isArray(code) ? code[0] : code;
};

export const BizsolCallBackPage = () => {
  const { rollbar } = useRollbarUtil();
  // === Environment Variables and Conditions ===
  const clientID = config.COGNITO_CLIENT_ID;
  const redirectURI = config.BIZSOL_REDIRECT_URI;
  const app = config.APP;
  const syncWithBizsolEmail = config.IS_SHIKOKUBANK; // Bizsolに登録しているEmailアドレスをMikatano IDでも利用する
  const shouldCheckContractAdminStatus = config.IS_SHIKOKUBANK; // ログイン時に必要に応じて契約管理者権限の付与・剥奪をする

  // === States and Hooks ===
  const { setSignInUserSession, signOut, userInfo } = useContext(AuthContext);
  const { authStatus } = useFixedAuth();
  const { notify } = useSnackBar();
  const router = useRouter();
  const [queryParams, setQueryParams] = useState<BizsolCallBackPageQueryParams>(
    {
      code: null,
      state: null,
      iss: null,
    }
  );
  const [bizsolAuthCodeStr, setBizsolAuthCodeStr] = useState<string | null>(
    null
  );
  const [queryParamStateUpdated, setQueryParamStateUpdated] =
    useState<boolean>(false); // Query Params が State に反映されたかどうか メインプロセスの useEffect が意図せず先に進むことを防ぐために使用
  const [calledSetSignInUserSession, setCalledSetSignInUserSession] = useState<
    boolean | null
  >(null);
  const [amIContractAdmin, actionAmIContractAdmin] =
    useAmIContractAdminNoCallback();
  const [calledActionAmIContractAdmin, setCalledActionAmIContractAdmin] =
    useState<boolean | null>(null);
  const [calledUpdateContractAdminStatus, setCalledUpdateContractAdminStatus] =
    useState<boolean | null>(null);
  const {
    loading: loadingUpdateContractAdminStatus,
    error: errorUpdateContractAdminStatus,
    result: resultUpdateContractAdminStatus,
    actionUpdateContractAdminStatus,
  } = useUpdateContractAdminStatus();
  const [showIDSwitchModal, setShowIDSwitchModal] = useState<boolean>(false);
  const [currentSSOBizsolUserSub, setCurrentSSOBizsolUserSub] = useState<
    string | null
  >(null);

  // === Mutations ===
  const [
    connectToBizsolMutation,
    { called: connectToBizsolCalled, loading: connectToBizsolLoading },
  ] = useConnectToBizsolMutation();

  const connectToBizsol = (authCode: string) => {
    connectToBizsolMutation({
      variables: {
        input: {
          client_id: clientID,
          code: authCode,
          redirect_uri: redirectURI,
        },
      },
      onCompleted: async (data) => {
        const token = data.connectToBizsol.token;
        const session: SessionInfo = {
          accessToken: token.access_token,
          idToken: token.id_token,
          refreshToken: token.refresh_token,
        };
        await setSignInUserSession(session);
        setCalledSetSignInUserSession(true); // ログインセッション更新後の処理を駆動する
        setLoginSource(LoginSource.bizsol);
        notify(`${config.BIZSOL_DISPLAY_NAME}との連携が完了しました`);
      },
      onError: config.IS_SHIKOKUBANK
        ? onConnectToBizsolErrorShikokubank(
            app,
            signOut,
            setShowIDSwitchModal,
            setCurrentSSOBizsolUserSub,
            rollbar
          )
        : config.IS_KYOSHIN
        ? onConnectToBizsolErrorKyoshin(app, signOut, rollbar)
        : handleError(rollbar),
    });
  };

  const [
    signInWithBizsolMutation,
    { called: signInWithBizsolCalled, loading: signInWithBizsolLoading },
  ] = useSignInWithBizsolMutation();

  const signInWithBizsol = (authCode: string) => {
    signInWithBizsolMutation({
      variables: {
        input: {
          client_id: clientID,
          code: authCode,
          redirect_uri: redirectURI,
        },
      },
      onCompleted: async (data) => {
        const authToken = data.signInWithBizsol.token;
        const registrationToken = data.signInWithBizsol.registrationToken;

        const queryParams = {};
        if (app) {
          queryParams["app"] = app;
        }

        // 認証トークンが得られた場合サインイン状態にする
        if (authToken) {
          const session: SessionInfo = {
            accessToken: authToken.access_token,
            idToken: authToken.id_token,
            refreshToken: authToken.refresh_token,
          };
          await setSignInUserSession(session);
          setCalledSetSignInUserSession(true); // ログインセッション更新後の処理を駆動する
          setLoginSource(LoginSource.bizsol);
          notify("ログインが完了しました");
          return;
        }

        // 認証トークンが得られなかった場合、登録トークンを使い事業者またはユーザーを登録する
        if (!registrationToken) {
          // 想定外のケース
          moveToErrorPage(ErrorCode.BIZSOL_INTERNAL_SERVER_ERROR);
          return;
        }
        queryParams["code"] = registrationToken.code;

        if (registrationToken.type == RegistrationTokenType.Company) {
          //事業者登録フローへ進む
          if (syncWithBizsolEmail) {
            // - BizsolのEmailを再利用する場合は直接登録画面に遷移
            router.push({
              pathname: "/bizsol/register-company",
              query: queryParams,
            });
          } else {
            // - BizsolのEmailとは個別でMikatanoにEmailを登録する場合は、仮登録画面に遷移
            router.push({
              pathname: "/bizsol/provisional-registration",
              query: queryParams,
            });
          }
          return;
        }
        if (registrationToken.type == RegistrationTokenType.User) {
          // ユーザー登録フローへ進む
          router.push({
            pathname: "/bizsol/register-user",
            query: queryParams,
          });
          return;
        }

        // 想定外のケース
        moveToErrorPage(ErrorCode.BIZSOL_INTERNAL_SERVER_ERROR);
      },
      // すでにログイン済みの場合は、onCompleted が呼ばれないので、ここで処理する
      onError: config.IS_SHIKOKUBANK
        ? onSignInWithBizsolErrorShikokubank(rollbar)
        : config.IS_KYOSHIN
        ? onSignInWithBizsolErrorKyoshin(rollbar)
        : handleError(rollbar),
    });
  };

  const [switchBizsolIdMutation] = useSwitchBizsolIdMutation();
  const switchBizsolId = useMemo(() => {
    // 最新の Query Params を取得を利用するように関数をメモ化する
    return () => {
      // Query Params が State に反映されていない場合は処理を中断する
      if (!queryParamStateUpdated) {
        return;
      }

      switchBizsolIdMutation({
        variables: {
          input: {
            client_id: clientID,
            sso_bizsol_sub: currentSSOBizsolUserSub,
          },
        },
        onCompleted: async (data) => {
          // TODO: 大枠はこれでOK。setSignInUserSession で最新の cognito usr も取得できるはず, setCalledSetSignInUserSessionでログインセッション更新後の処理も駆動できているはず
          const token = data.switchBizsolID.token;
          const session: SessionInfo = {
            accessToken: token.access_token,
            idToken: token.id_token,
            refreshToken: token.refresh_token,
          };
          await setSignInUserSession(session);
          setCalledSetSignInUserSession(true); // ログインセッション更新後の処理を駆動する
          setLoginSource(LoginSource.bizsol);
          notify(
            `${config.BIZSOL_DISPLAY_NAME}IDの連携先の変更が完了しました。`
          );
        },
        onError: (err) => {
          // BizsolIDの連携先変更処理が失敗した場合は、通常のエラー画面へ遷移する
          handleError(rollbar)(err);
        },
      });
    };
  }, [
    queryParams,
    queryParamStateUpdated,
    bizsolAuthCodeStr,
    currentSSOBizsolUserSub,
  ]);

  // === Handlers ===
  const handleClickCancelInIDSwitchModal = () => {
    setShowIDSwitchModal(false);
  };

  const handleBizsolSwitchIDModal = () => {
    // 連携先変更処理を実行する
    switchBizsolId();
    // モーダルを閉じる
    setShowIDSwitchModal(false);
  };

  // === Side Effects ===
  // Set Query Params to State
  useEffect(() => {
    if (router.isReady) {
      const updatedQueryParams = {
        code: router.query.code,
        state: router.query.state,
        iss: router.query.iss,
      };

      // Set Query Params to State
      // code, state はbizsol のauth endpoint からのリダイレクト時に付与される。
      // iss はbizsol からの相互遷移機能によるリダイレクト時に付与される。
      setQueryParams({
        code: router.query.code,
        state: router.query.state,
        iss: router.query.iss,
      });

      // Update values that are made from Query Params
      const updatedCodeStr = getCodeStrFromQueryParamCode(
        updatedQueryParams.code
      );
      setBizsolAuthCodeStr(updatedCodeStr);

      // Record that Query Param State has been updated
      setQueryParamStateUpdated(true);
    }
  }, [router.isReady, router.query]);

  // Main Process
  useEffect(() => {
    if (!router.isReady) {
      return;
    }

    // Query Params が State に反映されていない場合は処理を中断する
    if (!queryParamStateUpdated) {
      return;
    }

    // ------------------------------------------------------------
    // (1) 相互遷移機能の処理 / en: Processing of Login to Bizsol and MikatanoID
    // - this process will end with following 1 case
    //   1. /bizsol/redirect-login
    // ------------------------------------------------------------
    // 相互遷移機能で遷移してきた場合、bizsol にリダイレクトして認可コードを取得する。
    if (queryParams.iss) {
      router.push("/bizsol/redirect-login");
      return;
    }

    // bizsol auth endpoint からのリダイレクトでない場合bizsol ログイン画面に遷移。
    if (!bizsolAuthCodeStr) {
      router.push("/bizsol/redirect-login");
      return;
    }

    // ------------------------------------------------------------
    // (2) Single Sign-On (SSO) with Bizsol
    // - this process will end with following 2 cases
    //   1. connectToBizsol
    //   2. signInWithBizsol
    // ------------------------------------------------------------
    if (authStatus == AuthStatus.Loading) {
      return;
    }

    if (connectToBizsolCalled || signInWithBizsolCalled) {
      // signInWithBizsol.onCompleted でサインイン状態になるとuseEffect が実行されconnectToBizsol が呼ばれてしまうため、
      // どちらかが既に呼ばれた場合はreturn する。
      return;
    }

    // ログイン状態の場合、bizsol 未連携と仮定して連携フローに進む。
    if (authStatus == AuthStatus.SignedIn) {
      connectToBizsol(bizsolAuthCodeStr);
      return;
    }

    // 未ログイン状態の場合、bizsol 連携済みと仮定してログインフローに進む。
    signInWithBizsol(bizsolAuthCodeStr);
  }, [
    authStatus,
    router.isReady,
    queryParams,
    queryParamStateUpdated,
    bizsolAuthCodeStr,
  ]);

  // After sign-in, Update ContractAdmin Status and go to home
  useEffect(() => {
    console.log(`
      router.isReady=${router.isReady};
      authStatus=${authStatus}
      connectToBizsolCalled=${connectToBizsolCalled};
      conneccToBizsolLoading=${connectToBizsolLoading};
      signInWithBizsolCalled=${signInWithBizsolCalled};
      signInWithBizsolLoading=${signInWithBizsolLoading};
      calledSetSignInUserSession=${calledSetSignInUserSession};
      userInfo=${userInfo};
      amIContractAdmin=${amIContractAdmin};
      calledUpdateContractAdminStatus=${calledUpdateContractAdminStatus};
      loadingUpdateContractAdminStatus=${loadingUpdateContractAdminStatus};
      errorUpdateContractAdminStatus=${errorUpdateContractAdminStatus};
      resultUpdateContractAdminStatus=${resultUpdateContractAdminStatus};
      showIDSwitchModal=${showIDSwitchModal}
      shouldCheckContractAdminStatus=${shouldCheckContractAdminStatus}
      `);

    if (!router.isReady) {
      return;
    }

    // signIn 完了後にしか実行しない
    if (authStatus != AuthStatus.SignedIn) {
      return;
    }

    if (!connectToBizsolCalled && !signInWithBizsolCalled) {
      // signInWithBizsol or connectToBizsol が実行されていない場合は何もしない
      // en: wait for EITHER connectToBizsol or signInWithBizsol to be called
      return;
    }

    if (connectToBizsolCalled && connectToBizsolLoading) {
      // connectToBizsolが実行されている場合は待機
      // if connectToBizsol is called and still loading, wait
      return;
    }

    if (signInWithBizsolCalled && signInWithBizsolLoading) {
      // signInToBizsolが実行されている場合は待機
      // if signInToBizsol is called and still loading, wait
      return;
    }

    // kyoshin bank only needs connectToBizsol/signInWithBizsol, once either is done, go to select-service
    if (!shouldCheckContractAdminStatus) {
      // サービスページに遷移する
      router.push(getUrlWithQuery("/select-service", app));
      return;
    }

    // ID連携のやり直し用モーダルが表示されている場合は次の画面に遷移せず待機
    if (showIDSwitchModal) {
      return;
    }

    // [四国銀行の特別対応]
    // - 四国銀行は、ログイン時にBiz権限とMikatano ID権限を同期させる特殊仕様がある
    // - その影響で、通常のフローだと契約管理者が0人になる恐れがある
    // - それを抑止するため、特別に「Mi管理者ならば無条件で契約管理者権限も付与」という仕様になった
    //
    // [Note]
    // signInWithBizsol.onCompleted/connectToBizsol.onCompleted
    // 内で setSignInUserSession が呼ばれ、userInfo が非同期で更新される
    // (厳密には setSignInUserSession -> authStatus update -> userInfo update という流れ at AuthProvider)
    // userInfo は、未ログイン状態 undefined --> ログイン状態に変化することが期待される
    // 本処理は、 setSignInUserSession 実行後のレンダリングで呼び出されるが、
    // userInfo の更新までには時間差がある想定なので、userInfo の更新完了後に諸々の処理を実行する

    // userInfo にログインしたユーザーの情報が格納されていなければ実行しない
    if (!userInfo || !userInfo.sub) {
      return;
    }

    // 現在の契約管理者権限が未取得であれば取得する
    if (amIContractAdmin == null && !calledActionAmIContractAdmin) {
      actionAmIContractAdmin(userInfo.sub);
      setCalledActionAmIContractAdmin(true);
      return;
    }

    if (amIContractAdmin == null) {
      // actionAmIContractAdmin が完了していなければ待機
      return;
    }

    // 最新のBizsol権限によって更新されたかもしれない現在のMikatanoID権限と
    // 現在の自身の契約管理者ステータスに基づいて
    // 契約管理者権限の付与 or 剥奪を行う
    // 更新の必要がなければ、何もしない
    // 1度だけ実行
    if (!calledUpdateContractAdminStatus) {
      actionUpdateContractAdminStatus(
        userInfo.sub,
        userInfo.authority,
        amIContractAdmin
      );
      setCalledUpdateContractAdminStatus(true);
      return;
    }

    // actionUpdateContractAdminStatus が完了していなければ待機
    if (loadingUpdateContractAdminStatus == true) {
      return;
    }

    // actionUpdateContractAdminStatus が完了してエラーならばエラーメッセージを表示
    if (
      loadingUpdateContractAdminStatus == false &&
      errorUpdateContractAdminStatus
    ) {
      handleError(rollbar)(errorUpdateContractAdminStatus);
      return;
    }

    // actionUpdateContractAdminStatus が完了して正常終了ならば次の画面に遷移
    if (
      loadingUpdateContractAdminStatus == false &&
      resultUpdateContractAdminStatus
    ) {
      // サービスページに遷移する
      router.push(getUrlWithQuery("/select-service", app));
      return;
    }
  }, [
    router.isReady,
    authStatus,
    connectToBizsolCalled,
    connectToBizsolLoading,
    signInWithBizsolCalled,
    signInWithBizsolLoading,
    calledSetSignInUserSession,
    userInfo,
    amIContractAdmin,
    calledActionAmIContractAdmin,
    calledUpdateContractAdminStatus,
    loadingUpdateContractAdminStatus,
    errorUpdateContractAdminStatus,
    resultUpdateContractAdminStatus,
    showIDSwitchModal,
  ]);

  // === Render ===
  return (
    <>
      {showIDSwitchModal && (
        <BizsolSwitchIDModal
          active={showIDSwitchModal}
          bizsolDisplayName={config.BIZSOL_DISPLAY_NAME}
          onClickCancel={handleClickCancelInIDSwitchModal}
          onClickSwitch={handleBizsolSwitchIDModal}
        />
      )}
    </>
  );
};

const onConnectToBizsolErrorShikokubank = (
  app: string,
  signOut: () => void,
  setShowIDSwitchModal: (show: boolean) => void,
  setCurrentSSOBizsolUserSub: (sub: string | null) => void,
  rollbar: Rollbar
) => {
  return (err: ApolloError) => {
    // Mikatano ID ログイン済みでBizsol連携待ちではない場合、適切な処理を行う

    // [一時的に機能をOFF]
    // https://github.com/moneyforward/mfx_id/pull/1614 によりBackendからも当該エラーコードが返らないようになっている
    // ----
    // SSOしてきたBizsolユーザーが既に他のMikatanoIDと連携済みだった場合
    // ID連携のやり直しを問いかける
    if (
      errorCodeIsIncluded(
        err,
        ErrorCode.BIZSOL_USER_IS_ALREADY_CONNECTED_TO_ANOTHER_MIKATANO_ID
      )
    ) {
      // ID連携のやり直しを問いかける
      console.log("show modal");
      setShowIDSwitchModal(true);
      setCurrentSSOBizsolUserSub(
        extractErrExtraInfo(err, BackendErrorExtraInfoKeys.BIZSOL_SUB)
      );
      return;
    }

    // 「Bizsol連携済みMikatanoユーザー」でログイン済み ＆ SSOしてきたBizユーザーと異なるBizsolユーザーと連携済み の場合
    // ログイン済みでsubが異なる場合サインアウトさせる
    if (
      errorCodeIsIncluded(
        err,
        ErrorCode.BIZSOL_USER_IS_CONNECTED_TO_ANOTHER_SUBJECT
      )
    ) {
      signOut();
    }

    // ログイン済み & 連携済み の場合はエラーにせずログイン状態を維持する
    if (errorCodeIsIncluded(err, ErrorCode.BIZSOL_USER_IS_ALREADY_CONNECTED)) {
      // SSOしてきたBizsolユーザーが、ログイン中のMikatanoユーザーと連携済みなので、
      // 単純にログインを継続する
      Router.push({
        pathname: "/select-service",
        query: { app: app },
      });
      return;
    }

    // 上記以外は、エラーとして扱う
    handleError(rollbar)(err);
  };
};

const onConnectToBizsolErrorKyoshin =
  (app: string, signOut: () => void, rollbar: Rollbar) =>
  (err: ApolloError) => {
    // == エラーとみなす場合 ==
    // ログイン済みでsubが異なる場合サインアウトさせる
    if (
      errorCodeIsIncluded(
        err,
        ErrorCode.BIZSOL_USER_IS_CONNECTED_TO_ANOTHER_SUBJECT
      )
    ) {
      signOut();
      handleError(rollbar)(err); // サインアウト後にエラーを表示する
    }

    // == エラーとみなさない場合 ==
    // ログイン済み & 連携済み の場合はエラーにせず次に移動する
    if (errorCodeIsIncluded(err, ErrorCode.BIZSOL_USER_IS_ALREADY_CONNECTED)) {
      // ログイン元をBizsolに上書きする
      // -> このパスを通る時点で、 LastLoginSource は Mikatano になっているはず
      setLoginSource(LoginSource.bizsol);
      // 正常処理として次のページに遷移する
      Router.push({
        pathname: "/select-service",
        query: { app: app },
      });
      return;
    }
    // ログイン済み & NOT 連携済み の場合はエラーにせずBizsol(IB)に移動する
    Router.push({
      pathname: "/bizsol/redirect-top",
    });
    return;
  };

const onSignInWithBizsolErrorShikokubank =
  (rollbar: Rollbar) => (err: ApolloError) => {
    if (
      errorCodeIsIncluded(
        err,
        ErrorCode.BIZSOL_USER_IS_NOT_ALLOWED_TO_BE_REGISTERED
      )
    ) {
      moveToErrorPage(ErrorCode.BIZSOL_USER_IS_NOT_ALLOWED_TO_BE_REGISTERED);
      return;
    }
    handleError(rollbar)(err);
  };

const onSignInWithBizsolErrorKyoshin =
  (rollbar: Rollbar) => (err: ApolloError) => {
    if (
      errorCodeIsIncluded(
        err,
        ErrorCode.BIZSOL_USER_IS_NOT_ALLOWED_TO_BE_REGISTERED
      )
    ) {
      Router.push({
        pathname: "/bizsol/redirect-top",
      });
      return; // early return することで、後続の画面表示が一瞬表示されるのを防ぐ
    }

    handleError(rollbar)(err);
  };

// bizsol 系のAPI に対する共通のエラー処理.
const handleError = (rollbar: Rollbar) => (err: ApolloError) => {
  const msg = extractBizsolRqeustErrorMessage(err);
  if (msg) {
    // Bizsol へのAPI Request が失敗した場合、メッセージをそのまま表示する
    moveToBizsolRequestErrorPage(msg);
    return;
  }

  const errorList: Array<ErrorCode> = [
    ErrorCode.BIZSOL_COMPANY_ALREADY_REGISTERED,
    ErrorCode.BIZSOL_USER_IS_NOT_ALLOWED_TO_REGISTER_COMPANY,
    ErrorCode.BIZSOL_USER_IS_NOT_ALLOWED_TO_SIGN_IN,
    ErrorCode.BIZSOL_USER_IS_NOT_ALLOWED_TO_CONNECT,
    ErrorCode.BIZSOL_TOKEN_IS_EXPIRED,
    ErrorCode.BIZSOL_INVALID_ID_TOKEN,
    ErrorCode.BIZSOL_USER_IS_CONNECTED_TO_ANOTHER_SUBJECT,
    ErrorCode.BIZSOL_USER_IS_NOT_LINKED_WITH_ANY_MIKATANO_ID,
  ];

  for (const code of errorList) {
    if (errorCodeIsIncluded(err, code)) {
      moveToErrorPage(code);
      return;
    }
  }

  rollbar.error("Failed to Bizsol API", err);
  moveToErrorPage(ErrorCode.BIZSOL_INTERNAL_SERVER_ERROR); // 上記にないエラーでもbizsol ボタンのあるエラーページに遷移する
};
